import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import IconifyIcon from '../../../wrappers/IconifyIcon';
import { useAuth } from '../../../../context/AuthContext';
import { adminHasAnyPermission } from '../../../../helpers/menu';
import {
  SCOPE_OPTIONS,
  allScopes,
  disableGoldTrading,
  emptyScopes,
  enableGoldTrading,
  getGoldTradingStatus,
  hasAllScopes,
  hasAnyScope,
  mapTradingApiError,
  normalizeScopes,
} from '../../../../services/trading';

const POLL_MS = 45_000;
const REASON_PLACEHOLDER = 'SafeGold scheduled maintenance';
const READ_PERMS = ['trading.killswitch.read', 'trading.killswitch.manage'];
const MANAGE_PERMS = ['trading.killswitch.manage'];

const UNTIL_PRESETS = [
  { value: 'manual', label: 'Until manually enabled' },
  { value: '2h', label: '2 hours' },
  { value: '3h', label: '3 hours' },
  { value: 'custom', label: 'Custom datetime' },
];

const fromDateTimeLocal = (value) => (value ? new Date(value).toISOString() : '');

const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  return (
    <div
      className={clsx(
        'position-fixed top-0 start-50 translate-middle-x mt-3 alert shadow alert-dismissible',
        `alert-${toast.type}`,
      )}
      style={{ zIndex: 9999, minWidth: 300 }}
    >
      <IconifyIcon
        icon={toast.type === 'success' ? 'bx:check-circle' : 'bx:error-circle'}
        className="me-2 fs-18 align-middle"
      />
      {toast.message}
      <button type="button" className="btn-close" onClick={onClose} />
    </div>
  );
};

const badgeFor = (enabled, scopes) => {
  if (enabled) return { label: 'Active', cls: 'badge-soft-success' };
  if (hasAllScopes(scopes)) return { label: 'Maintenance', cls: 'badge-soft-danger' };
  return { label: 'Partially', cls: 'badge-soft-warning' };
};

const initialScopes = (currentScopes, currentlyDisabled) => {
  const normalized = normalizeScopes(currentScopes);
  if (currentlyDisabled && hasAnyScope(normalized)) return normalized;
  if (hasAnyScope(normalized)) return normalized;
  return allScopes();
};

const DisableModal = ({
  show,
  onConfirm,
  onCancel,
  loading,
  currentScopes,
  currentlyDisabled,
}) => {
  const [reason, setReason] = useState(REASON_PLACEHOLDER);
  const [untilPreset, setUntilPreset] = useState('manual');
  const [customUntil, setCustomUntil] = useState('');
  const [scopes, setScopes] = useState(emptyScopes());

  useEffect(() => {
    if (!show) return;
    setReason(REASON_PLACEHOLDER);
    setUntilPreset('manual');
    setCustomUntil('');
    setScopes(initialScopes(currentScopes, currentlyDisabled));
  }, [show, currentScopes, currentlyDisabled]);

  const resolveUntil = () => {
    if (untilPreset === 'manual') return undefined;
    if (untilPreset === '2h') return new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
    if (untilPreset === '3h') return new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString();
    if (untilPreset === 'custom' && customUntil) return fromDateTimeLocal(customUntil);
    return undefined;
  };

  const toggleScope = (key) => {
    setScopes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = () => {
    const payload = { scopes: normalizeScopes(scopes) };
    const trimmed = reason.trim();
    if (trimmed) payload.reason = trimmed;
    const until = resolveUntil();
    if (until) payload.until = until;
    onConfirm(payload);
  };

  const customInvalid =
    untilPreset === 'custom' && customUntil && Number.isNaN(new Date(customUntil).getTime());
  const scopesOk = hasAnyScope(scopes);

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {currentlyDisabled ? 'Update gold trading disable' : 'Disable gold trading'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Choose which channels to block. Admin disable is selective and sticky —
          price recovery will <strong>not</strong> auto-enable these scopes.
        </p>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Channels to stop</Form.Label>
          <div className="border rounded p-3 bg-light bg-opacity-50">
            <div className="d-flex flex-column gap-2">
              {SCOPE_OPTIONS.map((opt) => (
                <div key={opt.key}>
                  <Form.Check
                    type="checkbox"
                    id={`navbar-gold-scope-${opt.key}`}
                    label={opt.label}
                    checked={Boolean(scopes[opt.key])}
                    onChange={() => toggleScope(opt.key)}
                    disabled={loading}
                  />
                  {opt.help && <div className="text-muted small ms-4">{opt.help}</div>}
                </div>
              ))}
            </div>
          </div>
          {!scopesOk && (
            <div className="text-danger small mt-2">Select at least one channel.</div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Reason (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={reason}
            placeholder={REASON_PLACEHOLDER}
            onChange={(e) => setReason(e.target.value)}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Label>Disable until</Form.Label>
          <Form.Select
            value={untilPreset}
            onChange={(e) => setUntilPreset(e.target.value)}
            disabled={loading}
          >
            {UNTIL_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {untilPreset === 'custom' && (
          <Form.Group className="mt-3 mb-0">
            <Form.Label>Custom datetime</Form.Label>
            <Form.Control
              type="datetime-local"
              value={customUntil}
              onChange={(e) => setCustomUntil(e.target.value)}
              disabled={loading}
              isInvalid={customInvalid}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={
            loading || !scopesOk || customInvalid || (untilPreset === 'custom' && !customUntil)
          }
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-1" />
              Saving…
            </>
          ) : currentlyDisabled ? (
            'Update disable'
          ) : (
            'Disable selected'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EnableModal = ({ show, onConfirm, onCancel, loading }) => (
  <Modal show={show} onHide={onCancel} centered>
    <Modal.Header closeButton>
      <Modal.Title>Enable gold trading</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="mb-0">This will resume all blocked buy / sell / autopay channels.</p>
      <p className="text-muted small mt-2 mb-0">
        Clears both admin and auto disable. Are you sure you want to proceed?
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button variant="success" onClick={onConfirm} disabled={loading}>
        {loading ? (
          <>
            <Spinner size="sm" className="me-1" />
            Enabling…
          </>
        ) : (
          'Enable trading'
        )}
      </Button>
    </Modal.Footer>
  </Modal>
);

const GoldTradingKillSwitch = () => {
  const { admin } = useAuth();
  const canRead = adminHasAnyPermission(admin, READ_PERMS);
  const canManage = adminHasAnyPermission(admin, MANAGE_PERMS);

  const [enabled, setEnabled] = useState(true);
  const [scopes, setScopes] = useState(normalizeScopes());
  const [message, setMessage] = useState(null);
  const [disabledBy, setDisabledBy] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [available, setAvailable] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showEnable, setShowEnable] = useState(false);
  const [toast, setToast] = useState(null);

  const toastTimer = useRef(null);

  const showToast = useCallback((type, msg) => {
    setToast({ type, message: msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const applyStatus = (data) => {
    setEnabled(Boolean(data?.enabled));
    setScopes(normalizeScopes(data?.scopes));
    setMessage(data?.message || null);
    setDisabledBy(data?.disabled_by || null);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getGoldTradingStatus();
      applyStatus(res?.data ?? res);
      setAvailable(true);
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        setAvailable(false);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const id = setInterval(fetchStatus, POLL_MS);
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchStatus]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  if (!loaded) return null;
  if (!canRead && !available) return null;

  const isDisabled = available && (!enabled || hasAnyScope(scopes));
  const interactive = available && canManage && !actionLoading;
  const badge = available
    ? badgeFor(enabled, scopes)
    : { label: 'N/A', cls: 'badge-soft-secondary' };

  const title = !available
    ? 'Gold trading status unavailable'
    : enabled
      ? 'Gold trading is active'
      : `Gold trading disabled${disabledBy ? ` (${disabledBy === 'admin' ? 'Admin' : 'Auto'})` : ''}${
          message ? `: ${message}` : ''
        }`;

  const handleToggle = () => {
    if (!interactive) return;
    if (enabled && !hasAnyScope(scopes)) setShowDisable(true);
    else setShowEnable(true);
  };

  const openDisable = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!interactive) return;
    setShowDisable(true);
  };

  const handleDisable = async (payload) => {
    setActionLoading(true);
    try {
      await disableGoldTrading(payload);
      showToast('success', 'Gold trading channels updated.');
      setShowDisable(false);
      await fetchStatus();
    } catch (err) {
      showToast('danger', mapTradingApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnable = async () => {
    setActionLoading(true);
    try {
      await enableGoldTrading();
      showToast('success', 'Gold trading has been fully enabled.');
      setShowEnable(false);
      await fetchStatus();
    } catch (err) {
      showToast('danger', mapTradingApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="topbar-item px-2" title={title}>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className={clsx('badge border-0', badge.cls)}
            onClick={openDisable}
            disabled={!interactive}
            title={canManage ? 'Disable / update scopes' : title}
          >
            {badge.label}
          </button>
          <Form.Check
            type="switch"
            id="gold-trading-kill-switch"
            className="mb-0"
            checked={available ? enabled && !hasAnyScope(scopes) : false}
            disabled={!interactive}
            onChange={handleToggle}
            aria-label={
              enabled && !hasAnyScope(scopes)
                ? 'Disable gold trading'
                : 'Enable gold trading'
            }
            label={<span className="d-none d-lg-inline small">Gold</span>}
          />
        </div>
      </div>

      {canManage && (
        <>
          <DisableModal
            show={showDisable}
            onCancel={() => !actionLoading && setShowDisable(false)}
            onConfirm={handleDisable}
            loading={actionLoading}
            currentScopes={scopes}
            currentlyDisabled={isDisabled}
          />
          <EnableModal
            show={showEnable}
            onCancel={() => !actionLoading && setShowEnable(false)}
            onConfirm={handleEnable}
            loading={actionLoading}
          />
        </>
      )}
    </>
  );
};

export default GoldTradingKillSwitch;
